import { faBolt, faListCheck, faPen } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useMatch, useNavigate } from "react-router-dom";
import {
  activateScorecards,
  getTournamentRounds,
  saveRound,
  selectCurrentRound,
  selectTournamentRounds,
  setCurrentRound,
  updateRound,
} from "../../reducers/roundSlice";
import {
  selectCurrentTournament,
  updateCurrentTournamentRound,
} from "../../reducers/tournamentSlice";
import { useCallback, useEffect, useMemo, useRef } from "react";

//a round's own label wherever it has one, otherwise the number it is named for
export const getRoundName = (round, linkedRounds) => {
  return round?.roundName
    ? round?.roundName
    : getRoundNumberName(round, linkedRounds);
};

//a round's own label stands on its own. without one it is named for its number
//and the sub round its siblings make it, which they are needed to work out.
export const getRoundLabel = (round, linkedRounds) => {
  return round?.roundName || `Round ${getRoundNumberName(round, linkedRounds)}`;
};

//a round that shares its number with another round is named for the sub round it
//was split into, so round 1 becomes round 1A and round 1B.
export const getRoundNumberName = (round, linkedRounds) => {
  const linked = getSubRounds(round, linkedRounds);
  const index = linked.findIndex?.((r) => r?.id === round?.id);

  if (round?.subRound) {
    return `${round?.number}${round?.subRound}`;
  }

  if (linked.length > 1 && index !== -1) {
    return `${round?.number}${getSubRoundName(round, index)}`;
  }

  return `${round?.number}`;
};

//the server splits rounds that share a number into sub rounds, A first, then B,
//and so on in the order they were added.
export const getSubRoundName = (round, index) => {
  return round?.subRound ?? String.fromCharCode?.(65 + index);
};

//linked rounds read in sub round order, A first.
export const sortLinkedRounds = (rounds) => {
  return [...(rounds ?? [])].sort((a, b) =>
    `${a?.subRound ?? ""}`.localeCompare(`${b?.subRound ?? ""}`)
  );
};

//every round under this round's number, itself included, in sub round order
export const getSubRounds = (round, rounds) => {
  return sortLinkedRounds(
    rounds?.filter?.((r) => r?.number === round?.number)
  );
};

//a round is linked once another round shares its number
export const isLinkedRound = (round, rounds) => {
  return getSubRounds(round, rounds).length > 1;
};

//rounds that share a number are sub rounds of each other and belong together
//under one round header.
export const getRoundGroups = (rounds) => {
  const groups = [];

  rounds?.forEach?.((round) => {
    const group = groups.find?.((g) => g.number === round?.number);
    if (group) {
      group.rounds.push(round);
    } else {
      groups.push({ id: round?.number, number: round?.number, rounds: [round] });
    }
  });

  groups.forEach?.((group) => {
    group.rounds = sortLinkedRounds(group.rounds);
  });

  return groups;
};

//linked rounds share a number, so the next round in the sequence comes after the
//highest number in use, not after the number of rounds.
export const getNextRoundNumber = (rounds) => {
  const highestNumber =
    rounds?.reduce?.(
      (highest, round) => Math.max(highest, parseInt(round?.number) || 0),
      0
    ) ?? 0;

  return highestNumber + 1;
};

//a linked round is a new round that shares an existing round's number. the
//server splits rounds that share a number into sub rounds (A/B/C, etc.).
export const getLinkedRound = (round) => {
  const activeRoundTees = round?.roundTees?.filter?.((rt) =>
    round?.tees?.some?.((t) => t.id === rt.teeId && t.active)
  );

  return {
    ...round,
    id: undefined,
    state: "draft",
    roundName: undefined,
    //the shared number is what makes this a sub round of the existing round.
    number: round?.number,
    roundTees: activeRoundTees,
  };
};

//a round that plays one nine-hole course twice has the same course and nine on
//both of its round courses
export const isSameNineTwice = (roundCourses) => {
  if (roundCourses === undefined || roundCourses?.length < 2) {
    return false;
  }

  const rc1 = roundCourses[0];
  const rc2 = roundCourses[1];
  return rc1.course.id === rc2.course.id && rc1.holesToPlay === rc2.holesToPlay;
};

export const primary = {
  type: "green",
};

export const secondary = {
  type: "light-grey",
};

export const useRoundHooks = () => {
  const allRounds = useSelector(selectTournamentRounds);

  const dispatch = useDispatch();

  const match = useMatch("/:tournamentID/*");

  const tournamentID = match?.params?.tournamentID;

  const updateTournamentRounds = useCallback(() => {
    if (tournamentID) {
      dispatch(getTournamentRounds(tournamentID));
    }
  }, [dispatch, tournamentID]);

  useEffect(() => {
    updateTournamentRounds();
  }, [updateTournamentRounds]);

  return { allRounds, updateTournamentRounds };
};

//a linked round is seeded into currentRound before navigating to the create form,
//so this holds on to what was set there and works out the facility, courses and
//tees it fills the form's selections with. every other way into the form starts
//from an empty round, whatever currentRound was left holding.
export const useLinkedRoundSeed = () => {
  const location = useLocation();
  const currentRound = useSelector(selectCurrentRound);

  //taken once, when the form opens, before the form resets currentRound
  const seed = useRef(location?.state?.isLinkedRound ? currentRound : {});

  return useMemo(() => {
    const seededRound = seed.current;
    const tees = seededRound?.tees?.filter?.((t) => t.active) ?? [];

    return {
      seededRound,
      //each tee is matched to the round course it's played on
      seededTees: tees.map?.((t) => ({
        ...t,
        courseID: seededRound?.roundCourses?.find?.((c) =>
          c?.tees?.map?.((ct) => ct.id).includes(t.id)
        )?.id,
      })),
      //without a facility there is nothing to fill the selections with
      seededFacility: seededRound?.facility?.id
        ? {
            ...seededRound?.facility,
            courses: [...(seededRound?.courses ?? [])],
          }
        : undefined,
      seededCourses: seededRound?.roundCourses ?? [],
    };
  }, []);
};

export const useRoundActionHooks = (round) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const tournament = useSelector(selectCurrentTournament);

  // const toggleLockRound = (round) => {
  //   dispatch(lockScoring(round.id)).then((response) => {
  //     let update = { ...round };
  //     update.scoringLocked = !round.scoringLocked;
  //     dispatch(updateRound(update));
  //     dispatch(updateCurrentTournamentRound(update));
  //   });
  // };

  const startRound = () => {
    if (round) {
      let update = { ...round };
      update.state = "live";
      dispatch(updateRound(update));
      dispatch(updateCurrentTournamentRound(update));
      dispatch(saveRound(update));
      dispatch(activateScorecards(round.id));
      navigate(`/${tournament.id}/rounds/${round.id}/scoring`);
    }
  };

  const editScorecards = (round) => {
    navigate(`/${tournament.id}/rounds/${round.id}/scoring`);
  };

  //an empty round that shares this round's number, so the server sets it up as a
  //sub round of this one. the copy action is what carries the details over.
  const addLinkedRound = (e) => {
    e?.stopPropagation?.();
    dispatch(setCurrentRound({ number: round?.number }));
    navigate(`/${tournament.id}/rounds/new`, {
      state: { isLinkedRound: true },
    });
  };

  const roundActions = [
    {
      ...secondary,
      title: "Edit Round",
      buttonIcon: faPen,
      enabled: () => {
        return true;
      },
      onClick: (e) => {
        navigate(`/${tournament.id}/rounds/${round.id}/details`);
        e?.stopPropagation?.();
      },
    },
    {
      ...secondary,
      title: "edit scorecards",
      buttonIcon: faPen,
      enabled: (round) => {
        return round.state === "live";
      },
      onClick: (e) => {
        editScorecards(round);
        e?.stopPropagation?.();
      },
    },
    {
      ...secondary,
      title: "Hole Assignments",
      buttonIcon: faBolt,
      enabled: (round) => {
        return !["live", "final"].includes(round.state);
      },
      onClick: (e) => {
        navigate(`/${tournament.id}/rounds/${round.id}/hole-assignments`);
        e?.stopPropagation?.();
      },
    },
    {
      ...secondary,
      title: "Team Check In",
      buttonIcon: faListCheck,
      enabled: (round) => {
        return round.state === "live";
      },
      onClick: (e) => {
        navigate(
          `/${tournament.id}/rounds/checkin?roundID=${
            round.id
          }&roundName=${getRoundName(round)}`,
        );
        e?.stopPropagation?.();
      },
    },
    {
      ...primary,
      title: "Start Round",
      enabled: (round) => {
        return round.state === "ready";
      },
      onClick: (e) => {
        startRound(round);
        e?.stopPropagation?.();
      },
    },
    {
      ...secondary,
      title: "Scorecards",
      buttonIcon: faPen,
      enabled: (round) => {
        return ["live", "final"].includes(round.state);
      },
      onClick: (e) => {
        navigate(`/${tournament.id}/rounds/${round.id}/scoring`);
        e?.stopPropagation?.();
      },
    },
    {
      ...secondary,
      title: "Leaderboards & Skins",
      enabled: (round) => {
        return ["final"].includes(round.state);
      },
      onClick: (e) => {
        navigate(`leaderboards`);
        e?.stopPropagation?.();
      },
    },
  ];

  const currentRoundActions = roundActions.filter?.((r) => r.enabled(round));

  const priorityActions =
    currentRoundActions?.length > 2
      ? currentRoundActions.slice(-2)
      : currentRoundActions;

  return { currentRoundActions, priorityActions, addLinkedRound };
};
